// components/BuscadorClienteFactura/index.js
import React, { useState, useCallback, useEffect } from 'react';
import { Autocomplete, Box } from '@mui/material';
import FiltrosPopup from './FiltrosPopup';
import ChipsFiltros from './ChipsFiltros';
import OpcionCliente from './OpcionCliente';
import TextFieldPersonalizado from './TextFieldPersonalizado';
import useBusquedaClientes from './useBusquedaClientes';
import { 
  OPCIONES_FILTROS, 
  FILTROS_POR_DEFECTO, 
  FACTURACION_PROPIA 
} from './constants';

const BuscadorClienteFactura = ({ 
  value, 
  onChange, 
  style = {}, 
  size = 'small',
  label = "Facturar a",
  placeholder = "Buscar cliente..."
}) => {
  const [inputValue, setInputValue] = useState('');
  const [filtrosSeleccionados, setFiltrosSeleccionados] = useState(FILTROS_POR_DEFECTO);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Hook para la lógica de búsqueda
  const { 
    resultados, 
    cargando, 
    error,
    debouncedSearch,
    limpiarResultados 
  } = useBusquedaClientes(filtrosSeleccionados);

  // Placeholder dinámico
  const getPlaceholderText = () => {
    if (filtrosSeleccionados.length === 0) {
      return "Seleccione filtros para buscar...";
    }

    if (filtrosSeleccionados.length === OPCIONES_FILTROS.length) {
      return "Buscar cliente en todos los campos...";
    }

    const filtrosLabels = filtrosSeleccionados.map((filtro) => {
      const opcion = OPCIONES_FILTROS.find((f) => f.value === filtro);
      return opcion?.label
        .toLowerCase()
        .replace(/[👤📋🆔🔢🏠🏙️📞]/g, "")
        .trim();
    });

    return `Buscar por: ${filtrosLabels.join(", ")}...`;
  };

  // Efecto para buscar cuando cambia el término o filtros
  useEffect(() => {
    if (inputValue.length >= 2 && filtrosSeleccionados.length > 0) {
      debouncedSearch(inputValue);
    } else {
      limpiarResultados();
    }

    return () => {
      debouncedSearch.cancel?.();
    };
  }, [inputValue, filtrosSeleccionados, debouncedSearch, limpiarResultados]);

  // Manejo de cambio de filtros
  const handleFiltroClick = useCallback((filtro) => {
    setFiltrosSeleccionados(prev => {
      if (prev.includes(filtro)) {
        return prev.filter(f => f !== filtro);
      } else {
        return [...prev, filtro];
      }
    });
  }, []);

  // Manejo de selección
  const handleSeleccion = useCallback((event, newValue) => {
    if (newValue?.id === FACTURACION_PROPIA.id) {
      // Facturación propia
      onChange(FACTURACION_PROPIA);
      setInputValue(FACTURACION_PROPIA.razon_social);
    } else {
      onChange(newValue);
      setInputValue(newValue ? newValue.razon_social : '');
    }
  }, [onChange]);

  // Limpiar búsqueda
  const handleLimpiar = useCallback(() => {
    setInputValue('');
    limpiarResultados();
    onChange(null);
  }, [limpiarResultados, onChange]);

  // Valor para el autocomplete
  const getOptionLabel = useCallback((option) => {
    if (!option) return '';
    if (option.id === FACTURACION_PROPIA.id) return FACTURACION_PROPIA.razon_social;
    return option.razon_social || '';
  }, []);

  // Opciones incluyendo facturación propia
  const opciones = [FACTURACION_PROPIA, ...resultados];

  return (
    <Box sx={{ position: 'relative', ...style }}>
      {/* Popup de filtros */}
      {mostrarFiltros && (
        <FiltrosPopup
  filtrosSeleccionados={filtrosSeleccionados}
  onFiltroClick={handleFiltroClick}
  open={mostrarFiltros}
  onClose={() => setMostrarFiltros(false)}
  // Estas props hacen que se muestre ARRIBA:
  top="auto"
  bottom="100%"
  // O simplemente usa:
  anchorPosition="top" // Esto automáticamente posiciona arriba
/>
      )}

      {/* Campo de búsqueda principal */}
      <Autocomplete
        loading={cargando}
        disabled={filtrosSeleccionados.length === 0}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        value={value}
        onChange={handleSeleccion}
        options={opciones}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        filterOptions={(options) => options}
        noOptionsText={
          inputValue.length < 2
            ? "Escriba al menos 2 caracteres..."
            : filtrosSeleccionados.length === 0
            ? "Seleccione al menos un filtro"
            : cargando
            ? "Buscando..."
            : "No se encontraron resultados"
        }
        loadingText="Buscando clientes..."
        renderOption={(props, option) => (
          <OpcionCliente key={option.id} option={option} props={props} />
        )}
        renderInput={(params) => (
          <TextFieldPersonalizado
            params={params}
            label={label}
            placeholder={getPlaceholderText()}
            size={size}
            error={filtrosSeleccionados.length === 0 || !!error}
            helperText={
              error 
                ? error 
                : filtrosSeleccionados.length === 0
                ? "Seleccione al menos un filtro"
                : ""
            }
            cargando={cargando}
            inputValue={inputValue}
            onToggleFiltros={() => setMostrarFiltros(!mostrarFiltros)}
            onLimpiar={handleLimpiar}
            mostrarFiltros={mostrarFiltros}
          />
        )}
      />

      {/* Chips de filtros activos */}
      <ChipsFiltros
        filtrosSeleccionados={filtrosSeleccionados}
        onFiltroDelete={handleFiltroClick}
      />
    </Box>
  );
};

export default BuscadorClienteFactura;